import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail, MapPin, Phone } from "lucide-react";
import { useContent, imgUrl } from "@/context/ContentContext";

export default function Footer() {
  const { site, policies } = useContent();
  const mapHref = `https://maps.google.com/?q=${encodeURIComponent(`${site.address_line1}, ${site.address_line2}`)}`;
  return (
    <footer className="mt-24 bg-charcoal text-cream relative overflow-hidden" data-testid="site-footer">
      <div className="splat splat-pink w-64 h-64 -top-20 -left-20 opacity-40"></div>
      <div className="splat splat-cyan w-72 h-72 -bottom-24 -right-24 opacity-40"></div>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid gap-12 md:grid-cols-3 relative">
        <div>
          <h2 className="font-display text-6xl md:text-7xl leading-none">
            <span className="text-terracotta-light">{site.footer_heading_line1}</span><br/>
            <span className="text-forest-light">{site.footer_heading_line2}</span>
          </h2>
          <p className="mt-6 text-cream/70 max-w-xs">{site.footer_tagline}</p>
        </div>
        <div className="grid gap-3 text-sm">
          <h3 className="text-cream text-xl font-serif mb-2">Studio</h3>
          <a className="flex items-start gap-2 text-cream/70 hover:text-terracotta-light" href={mapHref} target="_blank" rel="noreferrer" data-testid="footer-address">
            <MapPin size={16} className="mt-0.5"/> <span>{site.address_line1}<br/>{site.address_line2}</span>
          </a>
          <a className="flex items-center gap-2 text-cream/70 hover:text-terracotta-light" href={`mailto:${site.email}`} data-testid="footer-email"><Mail size={16}/> {site.email}</a>
          {site.phone && <a className="flex items-center gap-2 text-cream/70 hover:text-terracotta-light" href={`tel:${site.phone}`} data-testid="footer-phone"><Phone size={16}/> {site.phone}</a>}
          <div className="flex items-center gap-3 mt-4">
            {site.instagram_link && <a href={site.instagram_link} target="_blank" rel="noreferrer" className="p-2 rounded-full bg-white/10 hover:bg-terracotta transition-colors" aria-label="Instagram"><Instagram size={16}/></a>}
            {site.facebook_link && <a href={site.facebook_link} target="_blank" rel="noreferrer" className="p-2 rounded-full bg-white/10 hover:bg-terracotta transition-colors" aria-label="Facebook"><Facebook size={16}/></a>}
          </div>
          {site.logo_image && <img src={imgUrl(site.logo_image)} alt={site.business_name} className="mt-6 h-20 w-auto opacity-90"/>}
        </div>
        <div className="grid gap-2 text-sm">
          <h3 className="text-cream text-xl font-serif mb-2">Explore</h3>
          <Link to="/workshops" className="text-cream/70 hover:text-forest-light">Book a Workshop</Link>
          <Link to="/private-events" className="text-cream/70 hover:text-forest-light">Private Events</Link>
          <Link to="/corporate" className="text-cream/70 hover:text-forest-light">Corporate & Mobile</Link>
          <Link to="/custom-rugs" className="text-cream/70 hover:text-forest-light">Custom Rugs</Link>
          <Link to="/gift-cards" className="text-cream/70 hover:text-forest-light">Gift Cards</Link>
          <Link to="/gallery" className="text-cream/70 hover:text-forest-light">Gallery</Link>
          {Object.entries(policies).map(([slug, p]) => <Link key={slug} to={`/policies/${slug}`} className="text-cream/70 hover:text-forest-light">{p.title}</Link>)}
        </div>
      </div>
      <div className="border-t border-white/10 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 flex flex-col sm:flex-row justify-between gap-2 text-xs text-cream/50">
          <div>© {new Date().getFullYear()} {site.business_name} All rights reserved.</div>
          <Link to="/admin/login" className="hover:text-forest-light">Studio Login</Link>
        </div>
      </div>
    </footer>
  );
}
